import React, { useState } from 'react';
import { usePortfolioPlConfigSlice } from 'widgets/portfolioPl/store/config/configSlice';
import { useSelectIsAppModeNewportSingleWidget } from 'app/store/general/selectors';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useDispatchUpdateAppSettings } from 'app/store/appSettings/dispatcher';
import AppSettingsEditor from 'appToolbar/AppSettingsEditor';
import './PortfolioPlSettings.scss';
import classNames from 'classnames';
import {
    earliestArrivalLabel,
    individualArrivalLabel,
    PnlBenchmark,
} from 'utils/PnL';
import {
    Dropdown,
    ToggleInput,
} from '@instinet-analytics/semantic-ui-components';
import { useDebouncedFunction } from '@instinet-analytics/core-lib';
import SymbolInput from 'symbols/components/SymbolInput';
import { DropdownItemProps } from '@instinet-analytics/semantic-ui-components';

export function PortfolioPlSettings() {
    const { config, dispatchConfigChanged, useToggleConfigOption } =
        usePortfolioPlConfigSlice();
    const { benchmarkType, index } = config;

    const isAppModeNewportSingleWidget =
        useSelectIsAppModeNewportSingleWidget();
    const appSettings = useSelectAppSettings();
    const dispatchUpdateAppSettings = useDispatchUpdateAppSettings();

    const [indexInputText, setIndexInputText] = useState(index);
    const [benchmarkTypeInputValue, setBenchmarkTypeInputValue] =
        useState(benchmarkType);

    const [debouncedConfigurationChange] = useDebouncedFunction(
        dispatchConfigChanged,
        2000,
        {
            callOnUnmount: true,
        }
    );

    const { value: leavesHidden, toggle: toggleLeavesHidden } =
        useToggleConfigOption('leavesHidden');

    const setBenchmarkTypeAndUpdateConfig = (benchmarkType: PnlBenchmark) => {
        setBenchmarkTypeInputValue(benchmarkType);
        debouncedConfigurationChange({ benchmarkType });
    };

    const setIndexAndUpdateConfig = (enteredText: string) => {
        const index = enteredText.toUpperCase();
        setIndexInputText(index);
        debouncedConfigurationChange({ index });
    };

    return (
        <div
            className={classNames(
                'PortfolioPlSettings',
                isAppModeNewportSingleWidget && 'single-widget'
            )}
        >
            <div className="group group-pnl">
                <div className="group-header">
                    <div className="title">
                        Profit & Loss (Performance) Graph
                    </div>
                </div>
                <div>
                    <div>Benchmarked to</div>
                    <div>
                        <BenchmarkTypeDropdown
                            value={benchmarkTypeInputValue}
                            onChanged={setBenchmarkTypeAndUpdateConfig}
                        />
                    </div>
                </div>
                <div>
                    <div>Comparison Index or Symbol</div>
                    <SymbolInput
                        value={indexInputText}
                        onChange={setIndexAndUpdateConfig}
                    />
                </div>
                {isAppModeNewportSingleWidget && (
                    <AppSettingsEditor
                        appSettings={appSettings}
                        onAppSettingsChanged={dispatchUpdateAppSettings}
                        specifyVisibleControls={{
                            symbology: true,
                        }}
                    />
                )}
            </div>

            <div className="group leaves-chart">
                <div className="group-header ">
                    <div className="title">Leaves</div>
                    <ToggleInput
                        checked={!leavesHidden}
                        onToggle={toggleLeavesHidden}
                    />
                </div>
                {isAppModeNewportSingleWidget && (
                    <AppSettingsEditor
                        appSettings={appSettings}
                        onAppSettingsChanged={dispatchUpdateAppSettings}
                        specifyVisibleControls={{
                            showsBuysAsPositive: true,
                            currency: true,
                        }}
                    />
                )}
            </div>

            {isAppModeNewportSingleWidget && (
                <AppSettingsEditor
                    appSettings={appSettings}
                    onAppSettingsChanged={dispatchUpdateAppSettings}
                    specifyVisibleControls={{
                        showChartHelp: true,
                        showTooltips: true,
                    }}
                />
            )}
        </div>
    );
}

function BenchmarkTypeDropdown(props: {
    value: PnlBenchmark;
    onChanged: (benchmarkType: PnlBenchmark) => void;
}) {
    const { value, onChanged } = props;

    interface BenchmarkDropdownItem {
        text: string;
        value: PnlBenchmark;
    }
    const benchmarkIndividualArrivalDropdownItem: BenchmarkDropdownItem = {
        text: individualArrivalLabel,
        value: PnlBenchmark.IndividualOrderArrival,
    };
    const benchmarkEarliestArrivalDropdownItem: BenchmarkDropdownItem = {
        text: earliestArrivalLabel,
        value: PnlBenchmark.Arrival,
    };
    const benchmarkLastDropdownItem: BenchmarkDropdownItem = {
        text: 'Last',
        value: PnlBenchmark.Last,
    };
    const benchmarkOpenDropdownItem: BenchmarkDropdownItem = {
        text: 'Open',
        value: PnlBenchmark.Open,
    };
    const benchmarkPncDropdownItem: BenchmarkDropdownItem = {
        text: 'Prev Close',
        value: PnlBenchmark.Pnc,
    };
    const benchmarkVwapDropdownItem: BenchmarkDropdownItem = {
        text: 'Vwap',
        value: PnlBenchmark.Vwap,
    };

    const benchmarkDropdownValues: DropdownItemProps[] = [
        benchmarkIndividualArrivalDropdownItem,
        benchmarkEarliestArrivalDropdownItem,
        benchmarkLastDropdownItem,
        benchmarkOpenDropdownItem,
        benchmarkPncDropdownItem,
        benchmarkVwapDropdownItem,
    ];

    return (
        <Dropdown
            options={benchmarkDropdownValues}
            value={value}
            onChange={onChanged}
        />
    );
}
